import { Breadcrumb, Flex } from 'antd';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetChannelInfoChannelInfoGetQuery } from '@shared/api';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { Routes } from '@shared/constants/routes';
import { ChannelUpdateForm } from './channel-update-form';
import styles from './channel-update-page.module.scss';
import { ScrollContainer } from '@shared/ui/scroll-container';

export const ChannelUpdatePage: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'channels' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { channelId } = useParams<{ channelId: string }>();
  const normalizedChannelId = Number(channelId);
  const preselectedAgentId = useMemo(() => {
    const rawAgentId = searchParams.get('ai-agent') ?? searchParams.get('ai-agent-id');
    const normalizedAgentId = Number(rawAgentId);

    if (!Number.isInteger(normalizedAgentId) || normalizedAgentId <= 0) {
      return undefined;
    }

    return normalizedAgentId;
  }, [searchParams]);
  const updateDraftKey = useMemo(() => {
    const agentKey = preselectedAgentId ?? 'no-agent';
    return `channels:update:${normalizedChannelId}:${agentKey}`;
  }, [normalizedChannelId, preselectedAgentId]);

  const { data, isLoading, isError, refetch } = useGetChannelInfoChannelInfoGetQuery(
    { channelId: normalizedChannelId },
    {
      skip: !normalizedChannelId,
      refetchOnMountOrArgChange: true,
    },
  );

  const breadcrumbKey = useMemo(() => {
    if (!data?.type) return null;
    return `breadcrumbs.${data.type}`;
  }, [data?.type]);

  useEffect(() => {
    if (!normalizedChannelId) {
      navigate(Routes.channels.root, { replace: true });
    }
  }, [navigate, normalizedChannelId]);

  if (isLoading) {
    return (
      <Flex vertical gap={20} className={styles.root}>
        <LoadingState />
      </Flex>
    );
  }

  if (isError || !data) {
    return (
      <Flex vertical gap={20} className={styles.root}>
        <FetchingErrorState refetch={refetch} />
      </Flex>
    );
  }

  return (
    <Flex vertical gap={20} className={styles.root}>
      <Breadcrumb
        items={[
          {
            title: t('channels'),
            onClick: () => navigate(Routes.channels.root),
          },
          {
            title: breadcrumbKey ? t(breadcrumbKey) : t('createChannel'),
          },
        ]}
      />

      <ScrollContainer>
        <Flex vertical gap={16} className={styles.container}>
          <ChannelUpdateForm
            channel={data}
            preselectedAgentId={preselectedAgentId}
            draftKey={updateDraftKey}
          />
        </Flex>
      </ScrollContainer>
    </Flex>
  );
};
